import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Ticket, Sparkles } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Subscriptions() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState({ paypal_enabled: false, paypal_client_id: null });
  const [current, setCurrent] = useState(null);
  const [voucher, setVoucher] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/subscription-plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    if (user) api.get("/me/dashboard").then((r) => setCurrent(r.data.subscription?.plan_id)).catch(() => {});
  }, [user]);

  const requireAuth = () => { if (!user) { toast.error("Please sign in to subscribe"); navigate("/login"); return false; } return true; };

  const pickFree = async () => {
    if (!requireAuth()) return;
    try { await api.post("/subscribe/free", { plan_id: "FREE" }); await refresh(); setCurrent("FREE"); toast.success("You're on the Free plan"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const demoActivate = async (planId) => {
    if (!requireAuth()) return;
    try {
      await api.post("/subscribe/demo", { plan_id: planId });
      await refresh(); setCurrent(planId);
      toast.success(`${planId} Premium activated (demo mode)`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const redeem = async () => {
    if (!requireAuth()) return;
    if (!voucher.trim()) return;
    try {
      const { data } = await api.post("/vouchers/redeem", { code: voucher });
      await refresh(); setCurrent(data.subscription.plan_id); setVoucher("");
      toast.success(`Voucher redeemed — ${data.plan.name} unlocked!`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Membership</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl mb-4">Choose your <span className="text-gradient-gold">Tazama Africa</span> plan</h1>
        <p className="text-white/60">Unlimited African cinema, series, documentaries and creator drops. Cancel anytime.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {plans.map((p) => {
          const isCurrent = current === p.id;
          const premium = p.id !== "FREE";
          const highlight = p.id === "R79";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
              className={`relative rounded-2xl p-6 border flex flex-col ${highlight ? "border-[#E50914] bg-gradient-to-b from-[#E50914]/10 to-transparent" : "border-white/10 bg-[#1A1724]"}`}>
              {p.badge && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold ${highlight ? "bg-[#E50914]" : "bg-[#D4AF37] text-black"}`}>{p.badge}</span>}
              <div className="flex items-center gap-2 mb-1">
                {premium ? <Crown className="w-5 h-5 text-[#D4AF37]" /> : <Sparkles className="w-5 h-5 text-white/60" />}
                <h3 className="font-display font-bold text-lg">{p.name}</h3>
              </div>
              <div className="flex items-baseline gap-1 my-3">
                <span className="font-display font-black text-4xl">{p.price}</span>
                <span className="text-white/50 text-sm">{p.period}</span>
              </div>
              <p className="text-sm text-white/50 mb-4 h-10">{p.description}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className="h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center font-semibold text-sm">Current Plan</div>
              ) : p.id === "FREE" ? (
                <button onClick={pickFree} data-testid="plan-free-btn" className="h-11 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 font-semibold text-sm transition-colors">Start Free</button>
              ) : (
                <button onClick={() => setSelected(p)} data-testid={`plan-select-${p.id}`}
                  className={`h-11 rounded-full font-semibold text-sm transition-colors ${highlight ? "bg-[#E50914] hover:bg-[#FF2A3B]" : "bg-white text-black hover:bg-white/90"}`}>
                  Subscribe {p.price}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Checkout for selected premium plan */}
      {selected && (
        <div className="max-w-md mx-auto glass rounded-2xl p-6 mb-12" data-testid="checkout-panel">
          <h3 className="font-display font-bold text-xl mb-1">Checkout — {selected.name}</h3>
          <p className="text-white/50 text-sm mb-4">{selected.price}{selected.period} · billed in USD via PayPal</p>
          {config.paypal_enabled ? (
            <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "USD" }}>
              <PayPalButtons style={{ layout: "vertical", color: "gold" }}
                createOrder={async () => {
                  const { data } = await api.post("/paypal/create-order", { plan_id: selected.id });
                  return data.order_id;
                }}
                onApprove={async (d) => {
                  await api.post("/paypal/capture", { order_id: d.orderID, plan_id: selected.id });
                  await refresh(); setCurrent(selected.id); setSelected(null);
                  toast.success(`${selected.name} activated!`);
                }}
                onError={() => toast.error("PayPal payment failed")}
              />
            </PayPalScriptProvider>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded-lg p-3">
                PayPal keys not configured yet. Use demo activation or a voucher to unlock premium now.
              </p>
              <button onClick={() => demoActivate(selected.id)} data-testid="demo-activate-btn"
                className="w-full h-11 rounded-full bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors">
                Activate {selected.name} (Demo)
              </button>
            </div>
          )}
          <button onClick={() => setSelected(null)} className="w-full mt-3 text-sm text-white/50 hover:text-white">Cancel</button>
        </div>
      )}

      {/* Voucher */}
      <div className="max-w-md mx-auto glass rounded-2xl p-6" data-testid="voucher-panel">
        <div className="flex items-center gap-2 mb-3"><Ticket className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-display font-bold text-lg">Redeem a voucher</h3></div>
        <p className="text-white/50 text-sm mb-4">Got a promo or gift code? Enter it to unlock premium access.</p>
        <div className="flex gap-2">
          <input value={voucher} onChange={(e) => setVoucher(e.target.value)} placeholder="TAZAMA-XXXXXX" data-testid="voucher-input"
            className="flex-1 h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#D4AF37]/60 focus:outline-none uppercase font-mono text-sm" />
          <button onClick={redeem} data-testid="voucher-redeem-btn" className="px-6 h-11 rounded-lg bg-[#D4AF37] text-black font-semibold hover:bg-[#F59E0B] transition-colors">Redeem</button>
        </div>
      </div>
    </div>
  );
}
